import { apiFetch, tokenStore } from "./client";

// Step DSL générique — on reste lâche côté frontend, le backend valide.
export type DSLStep = Record<string, unknown>;

export interface OCRItem {
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  confidence?: number;
}

export interface ScriptFailure {
  id: string;
  run_id: string;
  script_id: string;
  phone_id: string | null;
  script_name?: string | null;
  phone_name?: string | null;
  step_path: string;
  step_type: string;
  step_definition: DSLStep;
  preceding_steps: Array<{ path: string; type: string; step: DSLStep }>;
  error_message: string;
  error_kind: string | null;
  ocr_items: OCRItem[] | null;
  runtime_vars: Record<string, unknown> | null;
  has_screenshot: boolean;
  screenshot_bytes: number | null;
  created_at: string;
  rescue_attempts?: RescueAttempt[];
}

export interface RescueAttempt {
  id: string;
  failure_id?: string;
  model: string;
  prompt?: Record<string, unknown>;
  response_raw?: string;
  suggestion: {
    diagnosis: string;
    recovery_steps: DSLStep[];
    confidence?: "low" | "medium" | "high";
  } | null;
  validation: { ok: boolean; errors: string[] } | null;
  status: "pending" | "applied" | "ignored" | "errored";
  input_tokens: number | null;
  output_tokens: number | null;
  cost_cents: string | number | null;
  duration_ms: number | null;
  error_message: string | null;
  applied_at: string | null;
  created_at: string;
}

interface ListResponse {
  success: boolean;
  failures: ScriptFailure[];
  total: number;
}

interface SingleResponse {
  success: boolean;
  failure: ScriptFailure;
}

export async function listFailures(opts: {
  limit?: number;
  offset?: number;
  script_id?: string;
  phone_id?: string;
  error_kind?: string;
} = {}): Promise<{ failures: ScriptFailure[]; total: number }> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
  if (opts.script_id) qs.set("script_id", opts.script_id);
  if (opts.phone_id) qs.set("phone_id", opts.phone_id);
  if (opts.error_kind) qs.set("error_kind", opts.error_kind);
  const data = await apiFetch<ListResponse>(`/api/failures?${qs.toString()}`);
  return { failures: data.failures, total: data.total };
}

export async function getFailure(id: string): Promise<ScriptFailure> {
  const data = await apiFetch<SingleResponse>(`/api/failures/${id}`);
  return data.failure;
}

// Pour servir une image protégée par JWT sans token en query string, on
// fetch en mode authentifié et on crée une Object URL. Le caller doit
// révoquer l'URL au démontage (URL.revokeObjectURL).
export async function fetchFailureScreenshot(id: string): Promise<string> {
  const token = tokenStore.get() ?? "";
  const r = await fetch(`/api/failures/${id}/screenshot`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    throw new Error(`screenshot fetch failed (HTTP ${r.status})`);
  }
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}

export async function deleteFailure(id: string): Promise<void> {
  await apiFetch(`/api/failures/${id}`, { method: "DELETE" });
}

// ---- Rescue (Phase 1) ----

interface RescueResponse {
  success: boolean;
  rescue: RescueAttempt;
}

export async function askAi(
  failureId: string,
  model?: string
): Promise<RescueAttempt> {
  const data = await apiFetch<RescueResponse>(
    `/api/failures/${failureId}/ask-ai`,
    {
      method: "POST",
      body: JSON.stringify(model ? { model } : {}),
    }
  );
  return data.rescue;
}

export async function applyRescue(rescueId: string, dryRun = false): Promise<{
  recovery_steps: DSLStep[];
  target_step_path: string;
}> {
  return apiFetch(`/api/rescues/${rescueId}/apply`, {
    method: "POST",
    body: JSON.stringify({ dry_run: dryRun }),
  });
}

export async function ignoreRescue(rescueId: string): Promise<void> {
  await apiFetch(`/api/rescues/${rescueId}/ignore`, { method: "POST" });
}

export interface RescueModel {
  id: string;
  label: string;
}

export async function listRescueModels(): Promise<{
  default: string;
  models: RescueModel[];
}> {
  return apiFetch(`/api/rescues/models`);
}
